'use client';

import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
} from "react";
import axios from "axios";
import uuid from "draft-js/lib/uuid";
import {StatusType} from "../../../components/StatusType";
import {useParams, usePathname} from "next/navigation";
import {createNewDir, deleteFileById, uploadFileToServer} from "../../../api/FileAPI";

export interface Action {
  id: string;
  name: string;
  status: StatusType;
  size: number;
  progress: number;
}

interface RecentActionContextType {
  actions: Action[];
  openDrawer: boolean;
  setOpenDrawer: Dispatch<SetStateAction<boolean>>;
  addAction: (action: Action) => void;
  removeActionById: (id: string) => void;
  updateAction: (id: string, newProgress: number, status: StatusType) => void;
  uploadFile: (caseId: string, caseName: string, file: File, targetFileId: string) => Promise<any>;
  addNewDirectory: (caseId: string, directoryName: string, targetFileId: string) => Promise<any>;
  deleteFile: (fileId: string, fileName: string) => Promise<any>;
  changeOpenDrawer: () => void;
}

const RecentActionContext = createContext<RecentActionContextType>({} as RecentActionContextType);

export const RecentActionProvider = ({children}: {children: ReactNode;}) => {
  const [actions, setActions] = useState<Action[]>([]);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const params = useParams<{caseId: string;}>();
  const path = usePathname();

  useEffect(() => {
    const savedActions = localStorage.getItem("recentActions");
    setActions(savedActions ? JSON.parse(savedActions) : []);
  }, []);

  useEffect(() => {
    if(typeof localStorage !== "undefined") {
      localStorage.setItem("recentActions", JSON.stringify(actions));
    }
  }, [actions]);

  useEffect(() => {
    setActions([]);
    setOpenDrawer(false);
  }, [params?.caseId]);

  const addAction = (action: Action) => {
    setActions((prev) => [action, ...prev]);
    if(path?.includes('legal/newCase')) setOpenDrawer(false);
    else setOpenDrawer(true);
  };

  const changeOpenDrawer = () => {
    setOpenDrawer(!openDrawer);
  };

  const updateAction = (id: string, newProgress: number, status: StatusType) => {
    setActions((prevActions) =>
      prevActions.map((action) =>
        action.id === id
          ? {...action, progress: Math.min(newProgress, 100), status}
          : action
      )
    );
  };

  const removeActionById = (id: string) => {
    setActions((prev) => prev.filter((action) => action.id !== id));
  };

  const uploadFile = async (
    caseId: string,
    caseName: string,
    file: File,
    targetFileId: string
  ): Promise<any> => {
    const idAction = uuid();
    const newAction: Action = {
      id: idAction,
      name: file.name,
      status: StatusType.UPLOADING,
      size: file.size,
      progress: 0,
    };

    addAction(newAction);

    try {
      const response = await uploadFileToServer(
        caseId,
        targetFileId,
        file,
        (event) => {
          const total = event.total ?? 0;
          const current = Math.round((event.loaded / total) * 100);
          updateAction(idAction, current, newAction.status);
        });

      updateAction(idAction, 100, StatusType.SUCCESS);
      return response.data;
    } catch(error) {
      console.error(error);
      updateAction(idAction, 100, StatusType.ERROR);
    }
  };

  const addNewDirectory = async (
    caseId: string,
    directoryName: string,
    targetFileId: string
  ): Promise<any> => {
    const idAction = uuid();
    const newAction: Action = {
      id: idAction,
      name: directoryName,
      status: StatusType.ADD_NEW_DIRECTORY_IN_PROGRESS,
      size: 0,
      progress: 0,
    };

    addAction(newAction);

    try {
      const response = await createNewDir(caseId, targetFileId, directoryName);
      updateAction(idAction, 100, StatusType.ADD_NEW_DIRECTORY_SUCCESS);
      return response.data;
    } catch(error) {
      console.error(error);
      updateAction(idAction, 100, StatusType.ADD_NEW_DIRECTORY_ERROR);
    }
  };

  const deleteFile = async (fileId: string, fileName: string): Promise<any> => {
    const idAction = uuid();
    const newAction: Action = {
      id: idAction,
      name: fileName,
      status: StatusType.DELETE_FILE_IN_PROGRESS,
      size: 0,
      progress: 0,
    };

    addAction(newAction);

    try {
      const response = await deleteFileById(fileId);
      updateAction(idAction, 100, StatusType.DELETE_FILE_SUCCESS);
      return response.data;
    } catch(error) {
      updateAction(idAction, 100, StatusType.DELETE_FILE_ERROR);
    }
  };

  return (
    <RecentActionContext.Provider
      value={{
        actions,
        openDrawer,
        setOpenDrawer,
        addAction,
        removeActionById,
        updateAction,
        uploadFile,
        addNewDirectory,
        deleteFile,
        changeOpenDrawer
      }}
    >
      {children}
    </RecentActionContext.Provider>
  );
};

export const useRecentActions = () => useContext(RecentActionContext);
